import { useMemo, useState } from "react";
import { Loader2, Download, FileDown, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { SPECIES, SPECIES_CATEGORIES, ALL_CATEGORIES, catLabel } from "@/lib/taxonomy";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

export const CatalogExport = () => {
  const { settings } = useSettings();
  const [pets, setPets] = useState([]);
  const [cats, setCats] = useState([]);
  const [includeOut, setIncludeOut] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);

  const availableCats = useMemo(() => {
    if (!pets.length) return ALL_CATEGORIES;
    return Array.from(new Set(pets.flatMap((p) => SPECIES_CATEGORIES[p] || [])));
  }, [pets]);

  const togglePet = (slug) => setPets((p) => (p.includes(slug) ? p.filter((x) => x !== slug) : [...p, slug]));
  const toggleCat = (slug) => setCats((c) => (c.includes(slug) ? c.filter((x) => x !== slug) : [...c, slug]));

  const download = async () => {
    setLoading(true);
    try {
      const validCats = cats.filter((c) => availableCats.includes(c));
      const { data } = await api.post(
        "/admin/export-catalog",
        { pet_types: pets, categories: validCats, include_out_of_stock: includeOut },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `catalogo-${(settings.store_name || "tienda").toLowerCase().replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Catálogo PDF generado");
    } catch {
      toast.error("No se pudo generar el catálogo");
    } finally {
      setLoading(false);
    }
  };

  const shareWhatsApp = async () => {
    setSharing(true);
    const validCats = cats.filter((c) => availableCats.includes(c));
    const body = { pet_types: pets, categories: validCats, include_out_of_stock: includeOut };
    try {
      // Try native share with the actual PDF file (works great on mobile / WhatsApp)
      let fileShared = false;
      if (navigator.canShare) {
        try {
          const { data } = await api.post("/admin/export-catalog", body, { responseType: "blob" });
          const file = new File([data], `catalogo-${(settings.store_name || "tienda").toLowerCase().replace(/\s+/g, "-")}.pdf`, { type: "application/pdf" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: `Catálogo ${settings.store_name || ""}`.trim(), text: `Catálogo de ${settings.store_name || "The Pet Shopping Co"} 🐾` });
            fileShared = true;
          }
        } catch (e) {
          if (e && e.name === "AbortError") { setSharing(false); return; }
          fileShared = false;
        }
      }
      if (!fileShared) {
        // Fallback: upload PDF, get a public link and open WhatsApp with the link
        const { data } = await api.post("/admin/export-catalog", { ...body, as_url: true });
        const msg = `¡Hola! 🐾 Aquí tienes el catálogo de ${settings.store_name || "The Pet Shopping Co"}:\n${data.url}\n\nEscríbeme para hacer tu pedido.`;
        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
        toast.success("Catálogo listo para compartir por WhatsApp");
      }
    } catch {
      toast.error("No se pudo compartir el catálogo");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="bg-teal/25 border border-teal/50 rounded-2xl p-4 flex items-start gap-3">
        <FileDown className="w-5 h-5 text-coral shrink-0 mt-0.5" />
        <p className="text-sm text-charcoal">Genera un catálogo PDF con tu marca para enviar a clientes por WhatsApp. Elige especies y categorías; si no marcas nada, se incluye todo el catálogo disponible.</p>
      </div>

      <div>
        <p className="text-sm font-bold text-charcoal mb-2">Especies</p>
        <div className="flex flex-wrap gap-2">
          {SPECIES.map((s) => (
            <button
              key={s.slug}
              data-testid={`export-pet-${s.slug}`}
              onClick={() => togglePet(s.slug)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${pets.includes(s.slug) ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2] hover:bg-peach/50"}`}
            >
              <s.icon className="w-4 h-4" /> {s.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-bold text-charcoal mb-2">Categorías {pets.length ? "(según especies elegidas)" : ""}</p>
        <div className="flex flex-wrap gap-2">
          {availableCats.map((c) => (
            <button
              key={c}
              data-testid={`export-cat-${c}`}
              onClick={() => toggleCat(c)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${cats.includes(c) ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2] hover:bg-peach/50"}`}
            >
              {catLabel(c)}
            </button>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-charcoal">
        <input type="checkbox" checked={includeOut} onChange={(e) => setIncludeOut(e.target.checked)} className="w-4 h-4 accent-[#E08A7E]" />
        Incluir productos agotados
      </label>

      <div className="flex flex-wrap gap-3">
        <button
          data-testid="export-catalog-button"
          onClick={download}
          disabled={loading || sharing}
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-coral text-white font-bold hover:bg-coral-dark active:scale-95 transition-all disabled:opacity-60 card-shadow"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
          Descargar catálogo PDF
        </button>
        <button
          data-testid="share-catalog-whatsapp-button"
          onClick={shareWhatsApp}
          disabled={loading || sharing}
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-teal text-charcoal font-bold hover:brightness-95 active:scale-95 transition-all disabled:opacity-60 card-shadow"
        >
          {sharing ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageCircle className="w-5 h-5" />}
          Compartir por WhatsApp
        </button>
      </div>
    </div>
  );
};
